import * as ToastPrimitive from '@radix-ui/react-toast';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { cn } from '../../lib/utils';

type Variant = 'default' | 'success' | 'danger' | 'info';

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  variant?: Variant;
  durationMs?: number;
}

interface ToastContextValue {
  showToast: (t: Omit<ToastItem, 'id'>) => void;
}

const Ctx = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Outside the provider (e.g. early init): no-op so we never crash a render path.
    return { showToast: () => undefined };
  }
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([]);

  const showToast = useCallback((t: Omit<ToastItem, 'id'>) => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setItems((arr) => [...arr, { id, durationMs: 5000, variant: 'default', ...t }]);
  }, []);

  const remove = useCallback((id: string) => {
    setItems((arr) => arr.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <Ctx.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {items.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            duration={t.durationMs}
            onOpenChange={(open) => {
              if (!open) remove(t.id);
            }}
            className={cn(
              'pointer-events-auto relative grid w-full max-w-sm grid-cols-[auto_1fr_auto] items-start gap-3 rounded-md border bg-surface p-4 shadow-popover data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full',
              variantStyles(t.variant),
            )}
          >
            <VariantIcon variant={t.variant} />
            <div className="space-y-1">
              <ToastPrimitive.Title className="font-heading text-sm font-semibold text-ink">
                {t.title}
              </ToastPrimitive.Title>
              {t.description ? (
                <ToastPrimitive.Description className="text-sm text-ink-muted">
                  {t.description}
                </ToastPrimitive.Description>
              ) : null}
            </div>
            <ToastPrimitive.Close
              className="rounded-md p-1 text-ink-subtle opacity-70 hover:opacity-100"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col gap-2 p-4 sm:bottom-4 sm:right-4 sm:max-w-sm" />
      </ToastPrimitive.Provider>
    </Ctx.Provider>
  );
}

function variantStyles(v: Variant | undefined): string {
  switch (v) {
    case 'success':
      return 'border-success/30';
    case 'danger':
      return 'border-danger/30';
    case 'info':
      return 'border-info/30';
    default:
      return 'border-line';
  }
}

function VariantIcon({ variant }: { variant: Variant | undefined }): JSX.Element {
  const cls = 'h-4 w-4 mt-0.5';
  switch (variant) {
    case 'success':
      return <CheckCircle2 className={cn(cls, 'text-success')} />;
    case 'danger':
      return <AlertCircle className={cn(cls, 'text-danger')} />;
    case 'info':
      return <Info className={cn(cls, 'text-info')} />;
    default:
      return <Info className={cn(cls, 'text-ink-subtle')} />;
  }
}
